import { FilterTableData, TableTitle } from '@app/components/table/table.type';
import useTranslation from '@app/hooks/use-translation';
import { useGetStatisticTeamsInfoQuery } from '@app/store/api/qm-module/qm-statistic/qm-statistic-page.api';
import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { FormTabSwitcher } from '@ui';

import Table from '@app/components/table';

import { StatisticWidgetsRangeType } from '@app/interfaces/qm-module/qm-statistic/qm-statistic.type';

import { StatisticColorByStepCalc } from '@app/utils/statistic-color-by-step';

import RangeBlock from '../range-block';
import { useGetStatisticLinkObject } from '../../hooks/useGetStatisticLinkObject';
import { StatisticOutletContextType } from '../../type';

const TeamsTapContent = () => {
  const { t } = useTranslation('pages.qmManage.statistic');

  const { mform_id } = useParams();

  const [tableSort, changeTableSort] = useState<FilterTableData | undefined>({
    sortBy: 'team',
    sortDesc: true,
  });
  const { setNavigationItems } = useOutletContext<StatisticOutletContextType>();

  const { widgetsFilterBlock, updateUrl } = useGetStatisticLinkObject(setNavigationItems);

  const [localRange, setLocalRange] = useState(widgetsFilterBlock?.range);

  const { data: teamsStatisticData, isLoading } = useGetStatisticTeamsInfoQuery(
    {
      params: { mform_id: mform_id || '' },
      body: { range: localRange, ...tableSort },
    },
    { skip: !mform_id },
  );

  useEffect(() => {
    setLocalRange(widgetsFilterBlock?.range);
  }, [widgetsFilterBlock?.range]);

  function handlerDateDropTrigger(key: StatisticWidgetsRangeType['type']) {
    if (key === 'r') {
      // changeShowingModelDatePicker(true);
      return;
    }

    setLocalRange((prev) => {
      const changedObj = { dt_type: prev?.dt_type, type: key, parameters: [] as [] };
      updateUrl({ ...widgetsFilterBlock, range: changedObj });
      return changedObj;
    });
  }

  function handleChangeDtType(value: string) {
    setLocalRange((prev) => {
      const changedObj = {
        dt_type: value as 'start' | 'processing',
        type: prev?.type as any,
        parameters: prev?.parameters,
      };
      updateUrl({ ...widgetsFilterBlock, range: changedObj });
      return changedObj;
    });
  }

  const dtTypeValuesList = [
    { value: 'start', title: t('dt_type.start') },
    { value: 'processing', title: t('dt_type.processing') },
  ];

  const dynamicCols = useMemo(
    () =>
      [...(teamsStatisticData?.table?.[0]?.criteria || [])]
        .sort((a, b) => {
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        })
        ?.map<TableTitle>((criteria) => ({
          index: criteria.id,
          title: criteria.name,
          filter: true,
        })) || [],
    [teamsStatisticData?.table],
  );

  const tableColumns: TableTitle[] = [
    {
      title: t('name'),
      index: 'team',
      filter: true,
      size: 140,
      rowClassName: '!justify-start pl-1',
    },
    ...dynamicCols,
    { title: t('score_avg'), index: 'score_avg', size: 140, filter: true },
  ];

  const standardColor = StatisticColorByStepCalc;

  const tableData = teamsStatisticData?.table.map((table) => {
    const criteriaCols = table?.criteria?.reduce((acc, crit) => {
      acc = {
        ...acc,
        [crit.id]: (
          <div
            className="h-full w-full text-center"
            style={{
              background: standardColor({ max: crit.score_max_calc, score: crit.rating_avg }),
            }}
          >
            {crit.rating_avg}
          </div>
        ),
      };
      return acc;
    }, {});

    return {
      team: <div className="truncate">{table.team.name}</div>,
      score_avg: (
        <div
          className="h-full w-full text-center"
          style={{
            background: table.score_avg.conditional_formatting
              ? table.score_avg.conditional_formatting.color
              : standardColor({
                  max: table.score_avg.score_max_calc,
                  score: table.score_avg.value,
                }),
          }}
        >
          {table.score_avg.value}
        </div>
      ),
      ...criteriaCols,
    };
  });

  function onChangeFilter(data: FilterTableData | undefined) {
    changeTableSort(data);
  }

  return (
    <div>
      <div className="flex items-baseline justify-end gap-[20px] ">
        <div className="min-w-[500px] max-w-[500px]">
          <FormTabSwitcher
            classname="!bg-[#efefef]"
            valuesList={dtTypeValuesList}
            isActive={localRange?.dt_type || 'start'}
            onChange={handleChangeDtType}
          />
        </div>
        <RangeBlock
          activeKey={localRange?.type}
          rangeFromStor={localRange}
          onChange={(key) => handlerDateDropTrigger(key as StatisticWidgetsRangeType['type'])}
        />
      </div>
      <Table
        hideDragMode
        default_width={4.5}
        onFilter={onChangeFilter}
        isPending={isLoading}
        columns={tableColumns}
        dataSource={tableData}
      />
    </div>
  );
};

export default TeamsTapContent;
